import {
  FormHelperText,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ChangeEvent, FC } from 'react';
import { useDictionary, useIsDesktop } from 'Hooks';
import { illegalChars } from 'Regex';
import { MAX_NOTE_LENGTH } from 'Config';

export interface INoteTextField {
  value: string;
  onChange: (value: string) => void;
  error: boolean;
  title?: string;
  placeholder?: string;
  titleVariant?: TypographyProps['variant'];
  counterColor?: string;
}

export const NoteTextField: FC<INoteTextField> = ({
  value,
  onChange,
  error,
  title,
  placeholder,
  titleVariant,
  counterColor,
}) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const isDesktop = useIsDesktop();

  return (
    <>
      <Typography
        variant={titleVariant || 'body3'}
        component='label'
        htmlFor='request-note'
      >
        {title || dictionary.uploadFile.note}
      </Typography>
      <TextField
        id='request-note'
        aria-describedby={error ? 'request-note-error' : undefined}
        multiline
        rows={4}
        variant='outlined'
        placeholder={placeholder || dictionary.uploadFile.notePlaceholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onInput={(e: ChangeEvent<HTMLInputElement>) => {
          e.target.value = e.target.value.replace(illegalChars, '');
        }}
        inputProps={{
          maxLength: MAX_NOTE_LENGTH,
        }}
        error={error}
        helperText={`${value.length}/${MAX_NOTE_LENGTH}`}
        fullWidth
        margin='normal'
        required
        InputProps={{
          style: {
            background: '#fff',
            border: error ? '1px solid #ec0000' : '1px solid #B7CCFF',
            borderRadius: '8px',
            paddingBottom: '30px',
          },
          'aria-describedby': undefined,
        }}
        FormHelperTextProps={{
          style: {
            color: error ? '#ec0000' : counterColor || '#A020FF',
            textAlign: 'left',
            fontWeight: value.length === MAX_NOTE_LENGTH ? 'bold' : undefined,
          },
          'aria-hidden': true,
        }}
        sx={{
          '& .MuiInputBase-input': {
            zIndex: 9,
            paddingLeft: '1px',
          },
          '& .MuiFormHelperText-root': {
            position: 'relative',
            top: isDesktop ? '-40px' : '-30px',
            fontSize: isDesktop ? '18px' : '14px',
            zIndex: 0,
          },
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
        slotProps={{
          input: {
            style: {
              background: '#fff',
              border: error ? '1px solid #ec0000' : '1px solid #B7CCFF',
              borderRadius: '8px',
            },
            'aria-describedby': undefined,
          },

          htmlInput: {
            maxLength: MAX_NOTE_LENGTH,
          },

          formHelperText: {
            style: {
              color: error ? '#ec0000' : '#A020FF',
              textAlign: 'left',
              fontWeight: value.length === MAX_NOTE_LENGTH ? 'bold' : undefined,
            },
            'aria-hidden': true,
          },
        }}
      />
      {error && (
        <FormHelperText
          role='alert'
          error={error}
          id={'request-note-error'}
          sx={{ m: '-38px 0 18px', fontSize: '14px', textAlign: 'right' }}
        >
          {dictionary.uploadFile.noteErrorMsg}
        </FormHelperText>
      )}
    </>
  );
};
